import { IIban } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import {
  IContractMandateGroup,
  NO_CONTINUOUS_VALUE,
  TContractsById,
} from "@rentiohq/shared-frontend/dist/utils/contractMandateGroup.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { obfuscateIban } from "@rentiohq/shared-frontend/dist/utils/iban.utils";
import { EAssets } from "@rentiohq/web-shared-next/dist/assets";
import {
  Button,
  EIconAvatarVariant,
  IconAvatar,
  MainCard,
  Stack,
  Typography,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import { useNavigate } from "react-router-dom";
import { DIRECT_DEBITS_ROUTE } from "../../Payments.utils";
import { ContractDurationLink } from "../ContractDurationLink";
import { DirectDebitsStyles } from "./DirectDebits.styles";

interface IProps {
  ibans: IIban[];
  contractGroups: IContractMandateGroup[];
  contractsById: TContractsById;
}

interface IContractRowProps {
  contract?: IContract;
  activeIban?: IIban;
  getTitleForIban: (iban?: IIban) => string;
}

const ContractRow = ({
  contract,
  activeIban,
  getTitleForIban,
}: IContractRowProps) => {
  const sx = DirectDebitsStyles();

  return (
    <Stack direction="row" alignItems="center" gap={2}>
      <IconAvatar
        icon={EAssets.SynchronizeLightIcon}
        variant={activeIban ? undefined : EIconAvatarVariant.Warning}
      />
      <Stack direction="column">
        {contract && <ContractDurationLink contract={contract} />}
        <Typography sx={sx.content.title}>
          {getTitleForIban(activeIban)}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const DirectDebits = ({
  ibans,
  contractGroups,
  contractsById,
}: IProps) => {
  const navigate = useNavigate();
  const sx = DirectDebitsStyles();

  const hasNoIbans = ibans.length === 0;

  // A group whose mandate points at an account this list does not hold reads as
  // "no active mandate", so the avatar and the card color must read it that way too.
  const getActiveIbanForGroup = (group: IContractMandateGroup) =>
    group.activeBankAccountId === NO_CONTINUOUS_VALUE
      ? undefined
      : ibans.find(iban => iban.id === group.activeBankAccountId);

  const getTitleForIban = (iban?: IIban) =>
    iban
      ? getLocalizedText("tenant.payments.direct_debit.active_title", {
          iban: obfuscateIban(iban.iban),
        })
      : getLocalizedText("tenant.payments.direct_debit.no_active_title");

  // No contract known (yet), or no bank account at all: one generic row, with
  // the original heuristic as a fallback while contractGroups is loading.
  const fallbackActiveIban = ibans.find(iban => iban.mandateApprovedAt);

  const buttonLabel = hasNoIbans
    ? getLocalizedText("tenant.payments.direct_debit.add_button")
    : getLocalizedText("tenant.payments.direct_debit.manage_button");

  // A single contract gets its own row too, so the tenant always sees which
  // contract (and which period) the direct debit belongs to.
  const showContractRows = !hasNoIbans && contractGroups.length > 0;

  // The design tints the card only while a direct debit is running; with several
  // contracts one running mandate is enough.
  const hasActiveDirectDebit = showContractRows
    ? contractGroups.some(group => !!getActiveIbanForGroup(group))
    : !!fallbackActiveIban;

  return (
    <MainCard border sx={sx.wrapper(hasActiveDirectDebit)}>
      {/* The button sits under the rows, so a long list of contracts and a
          narrow screen never squeeze it next to the text. */}
      <Stack direction="column" gap={2}>
        <Stack direction="column" gap={1.5}>
          {showContractRows ? (
            contractGroups.map(group => (
              <ContractRow
                key={group.contractId}
                contract={contractsById[group.contractId]}
                activeIban={getActiveIbanForGroup(group)}
                getTitleForIban={getTitleForIban}
              />
            ))
          ) : (
            <Stack direction="row" alignItems="center" gap={2}>
              <IconAvatar
                icon={EAssets.SynchronizeLightIcon}
                variant={
                  fallbackActiveIban ? undefined : EIconAvatarVariant.Warning
                }
              />
              <Typography sx={sx.content.title}>
                {getTitleForIban(fallbackActiveIban)}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Button
          variant={EButtonVariants.Contained}
          sx={sx.content.button}
          onClick={() =>
            hasNoIbans
              ? navigate(`${DIRECT_DEBITS_ROUTE}/add-bank-account`, {
                  state: { referrer: DIRECT_DEBITS_ROUTE },
                })
              : navigate(DIRECT_DEBITS_ROUTE)
          }
        >
          {buttonLabel}
        </Button>
      </Stack>
    </MainCard>
  );
};
